import { useFeatures } from "@/api/app-configuration.api";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { TypedFilterGroup } from "@/data/filters/filter-types";
import { essentialsText } from "@/hooks/use-trial-period";
import { Link, ToOptions } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  filterGroup: TypedFilterGroup;
  link: ToOptions;
  isAdded?: boolean;
  disabled?: boolean;
  isAdvancedFilter?: boolean;
};

export function FilterGroupCard({
  filterGroup,
  link,
  isAdded = false,
  disabled = false,
  isAdvancedFilter = false,
}: Props) {
  const { canUseContacts, canUseAdvancedFilters } = useFeatures();
  const isRestricted =
    filterGroup.id === "contacts"
      ? !canUseContacts
      : isAdvancedFilter && !canUseAdvancedFilters;

  const shouldShowCheckmark = isAdded || disabled;
  const shouldDisableHover = isAdded || disabled;

  const [showTrialCTA, setShowTrialCTA] = useState(false);

  const content = (
    <>
      <motion.div
        className={twMerge(
          "border-theme-outline-variant flex aspect-square w-[70px] shrink-0 items-center justify-center border-r transition-colors",
          !shouldDisableHover &&
            "group-hover:bg-theme-primary group-hover:border-theme-primary",
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {isRestricted ? (
          <span className="text-theme-on-primary bg-theme-primary rounded-full px-2 py-1 text-[0.625rem]/2 font-bold">
            Pro
          </span>
        ) : shouldShowCheckmark ? (
          <Check className="size-8 text-green-600 transition-colors dark:text-green-400" />
        ) : (
          <ArrowRight className="text-theme-on-background size-5 opacity-55 transition-[color,opacity] group-hover:text-white group-hover:opacity-100" />
        )}
      </motion.div>

      <motion.div
        className="flex items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.2,
        }}
      >
        <span
          className={twMerge(
            "p-3 text-[11px] font-semibold",
            !shouldDisableHover && "transition-all group-hover:translate-x-1",
          )}
        >
          {filterGroup.title}
        </span>
      </motion.div>
    </>
  );

  return (
    <>
      <li className="relative">
        <div className="border-theme-outline-variant absolute inset-0 rounded-md border" />

        {disabled ? (
          <div className="group relative flex cursor-not-allowed overflow-hidden rounded-md opacity-60">
            {content}
          </div>
        ) : (
          <Link
            className="group relative flex overflow-hidden rounded-md"
            {...link}
            onClick={(e) => {
              if (!isRestricted) return;
              e.preventDefault();
              e.stopPropagation();
              setShowTrialCTA(true);
            }}
          >
            {content}
          </Link>
        )}
      </li>
      <TrialCTAModal
        title={essentialsText}
        isOpen={showTrialCTA}
        onOpenChange={setShowTrialCTA}
      />
    </>
  );
}
